import React, { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { TextInput, NumberInput, Button, Group, Box, Switch } from "@mantine/core";
import HttpClient from "../helpers/HttpClient";
import { useNavigate } from "react-router-dom";
import { notify } from "../components/NotificationUtils";
import { BookingType } from "../types/BookingModel";
import { ConfirmDialog } from "../components/ConfirmDialog";

const BookingTypeEdit: React.FC<{ id?: number }> = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [isUpdate, setIsUpdate] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const httpClient = new HttpClient<BookingType>("/bookingType");

  const form = useForm({
    initialValues: { id: 0 as number | string, title: "", multiplier: 1, isPrae: false },
    validate: {
      title: (v: string) => (v.trim() ? null : "Bezeichnung ist erforderlich"),
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          const result: any = await httpClient.getById(Number(id));
          form.setValues(result.data);
          setIsUpdate(true);
        }
      } catch {
        notify("Fehler", "Buchungsart nicht gefunden", "red");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return <div>Laden...</div>;

  const onSubmit = async (values: typeof form.values) => {
    try {
      const response = await httpClient.post(values as any);
      if (response.status !== 200 && response.status !== 201) throw new Error();
      notify("Erfolg", `Buchungsart ${isUpdate ? "aktualisiert" : "erstellt"}`, "green");
      navigate(-1);
    } catch {
      notify("Fehler", "Speichern fehlgeschlagen", "red");
    }
  };

  const onDelete = async () => {
    try {
      const res = await httpClient.delete(Number(id));
      if (res.status !== 200) throw new Error();
      notify("Erfolg", "Buchungsart gelöscht", "green");
      navigate(-1);
    } catch {
      notify("Fehler", "Löschen fehlgeschlagen", "red");
    }
  };

  return (
    <>
      <ConfirmDialog
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onDelete}
        title="Buchungsart löschen"
        message="Soll diese Buchungsart wirklich gelöscht werden? Verknüpfte Buchungen müssen vorher eine andere Art erhalten."
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
      />
      <Box mx="auto" maw={400} mt="lg">
        <form onSubmit={form.onSubmit(onSubmit)}>
          <TextInput
            label="Bezeichnung"
            placeholder="z.B. Zahlungseingang"
            mb="sm"
            {...form.getInputProps("title")}
          />
          <NumberInput
            label="Multiplikator"
            description="1 = schuldenerhöhend · −1 = schuldenminderd"
            allowDecimal
            decimalSeparator=","
            mb="md"
            {...form.getInputProps("multiplier")}
          />
          <Switch
            label="PRAE-relevante Buchungsart"
            description="Zahlungsausgänge dieser Art werden für den L19 ELDA-Export herangezogen"
            mb="md"
            checked={form.values.isPrae}
            onChange={(e) => form.setFieldValue('isPrae', e.currentTarget.checked)}
          />
          <Group mt="md">
            <Button type="submit">Speichern</Button>
            {isUpdate && (
              <Button type="button" color="red" variant="light" onClick={() => setConfirmOpen(true)}>
                Löschen
              </Button>
            )}
            <Button variant="subtle" onClick={() => navigate(-1)}>Abbrechen</Button>
          </Group>
        </form>
      </Box>
    </>
  );
};

export default BookingTypeEdit;
